import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { closeModal } from 'Core/redux/main/slice';
import { showToast } from 'Core/redux/toast/slice';
import { redux } from 'Core/redux';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { CarrierService } from '../../../service/api/carrier-service';

const { CARRIERS_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'carriers';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  filters: {
    searchText: '',
  },
  filterVisible: false,
  tileView: true,
};

export const fetchCarriers = createAsyncThunk(
  'carriers/fetchCarriers',
  async (_filters, { rejectWithValue }) => {
    try {
      const response = await CarrierService.getAllCarriers();
      if (response.status === 200) return response.response || [];
      return rejectWithValue(response.errorMessage?.message || 'Failed to load Carriers');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Carriers');
    }
  },
);

export const fetchCarrierById = createAsyncThunk(
  'carriers/fetchCarrierById',
  async (carrierId, { rejectWithValue }) => {
    try {
      const response = await CarrierService.getCarrierById(carrierId);
      if (response.status === 200) return response.response;
      return rejectWithValue(response.errorMessage?.message || 'Failed to load Carrier details');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Carrier details');
    }
  },
);

export const saveCarrier = createAsyncThunk(
  'carriers/saveCarrier',
  async (carrierPayload, { rejectWithValue }) => {
    try {
      const response = await CarrierService.saveCarrier(carrierPayload);
      if (response.status === 200) return response.response;
      return rejectWithValue(response.errorMessage?.message || 'Failed to save Carrier');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to save Carrier');
    }
  },
);

export const fetchCarriersWithFilters = () => (dispatch, _getState) => {
  dispatch(fetchCarriers());
};

export const { reducer, actions } = createSlice({
  name: 'carriers',
  initialState: INITIAL_STATE,
  reducers: {
    resetFilters: state => {
      state.filters = INITIAL_STATE.filters;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCarriers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCarriers.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload || [];
      })
      .addCase(fetchCarriers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(saveCarrier.fulfilled, (state, action) => {
        // API may return saved entity or just id; always refresh after save via listener below.
        state.lastSaved = action.payload;
      })
      .addCase(saveCarrier.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(saveCarrier.rejected),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(saveCarrier.fulfilled),
  effect: async (_action, listenerApi) => {
    listenerApi.dispatch(closeModal(CARRIERS_ADD_EDIT));
    listenerApi.dispatch(fetchCarriersWithFilters());
    listenerApi.dispatch(
      showToast({
        message: 'Carrier saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.injectReducer(STORE_KEY, reducer);

